import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';

const EMPTY_MALL_FORM  = { name: '', city: '', contactEmail: '' };
const EMPTY_ADMIN_FORM = { name: '', email: '', password: '', confirm: '' };
const EMPTY_RESET_FORM = { password: '', confirm: '' };

// ── Tap Status sub-component (Super Admin read-only oversight) ────────────────
function TapOnboardingSection({ mallId }) {
  const [status, setStatus] = useState(null);
  const [logs,   setLogs]   = useState(null); // null = not expanded, [] = loaded
  const [logVendorId, setLogVendorId] = useState(null);

  const loadStatus = useCallback(async () => {
    try {
      const r = await api.get(`/super-admin/malls/${mallId}/tap/status`);
      setStatus(r.data);
    } catch (_) {}
  }, [mallId]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const viewLogs = async (vendorId) => {
    if (logVendorId === vendorId) { setLogs(null); setLogVendorId(null); return; }
    try {
      const r = await api.get(`/super-admin/malls/${mallId}/vendors/${vendorId}/tap/logs`);
      setLogs(r.data.logs || []);
      setLogVendorId(vendorId);
    } catch (_) { setLogs([]); setLogVendorId(vendorId); }
  };

  const badge = (s) => {
    if (s === 'ACTIVE')       return <span style={{ color:'var(--green)',  fontSize:12 }}>● Active</span>;
    if (s === 'LEAD_CREATED') return <span style={{ color:'var(--accent)', fontSize:12 }}>⏳ KYC Pending</span>;
    if (s === 'REJECTED')     return <span style={{ color:'var(--red)',    fontSize:12 }}>✗ Rejected</span>;
    return <span style={{ color:'var(--text3)', fontSize:12 }}>○ Not Started</span>;
  };

  return (
    <div className="card" style={{ marginTop:20 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
        <div style={{ fontWeight:700, fontSize:15 }}>🏦 Tap Marketplace Status</div>
        <button className="btn btn-ghost btn-sm" onClick={loadStatus}>↻ Refresh</button>
      </div>

      {/* Connection summary */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:12, marginBottom:16 }}>
        {[
          { label:'Credentials', value: status?.configured ? '● Configured' : '⚠ Not Set', color: status?.configured ? 'var(--green)' : 'var(--accent)' },
          { label:'Connection Verified', value: status?.connectionVerifiedAt ? new Date(status.connectionVerifiedAt).toLocaleDateString() : '—', color:'var(--text2)' },
          { label:'Vendors Active / Total', value: status ? `${status.vendorCounts?.active ?? 0} / ${status.vendorCounts?.total ?? 0}` : '—', color:'var(--text2)' },
        ].map(s => (
          <div key={s.label} style={{ background:'var(--bg3)', borderRadius:8, padding:'12px 14px' }}>
            <div style={{ fontSize:11, color:'var(--text3)', marginBottom:4 }}>{s.label}</div>
            <div style={{ fontSize:13, fontWeight:600, color: s.color }}>{s.value}</div>
          </div>
        ))}
      </div>

      {status?.tapMarketplaceId && (
        <div style={{ fontSize:12, color:'var(--text3)', marginBottom:14 }}>
          Marketplace ID: <code style={{ color:'var(--text2)' }}>{status.tapMarketplaceId}</code>
          {status.tapQuickServeRetailerId && <> &nbsp;·&nbsp; QuickServe Destination: <code style={{ color:'var(--text2)' }}>{status.tapQuickServeRetailerId}</code></>}
        </div>
      )}

      <div style={{ padding:'10px 14px', background:'#1a2a1a', borderRadius:8, fontSize:12, color:'var(--text3)', marginBottom:16 }}>
        Mall Admin configures and manages Tap credentials and vendor onboarding. Super Admin has read-only visibility here.
      </div>

      {/* Per-vendor status */}
      {(status?.vendors || []).length > 0 && (
        <div>
          <div style={{ fontWeight:600, fontSize:13, marginBottom:8 }}>Vendor Tap Status</div>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
            <thead>
              <tr>
                {['Vendor','Status','Retailer ID','Logs'].map(h => (
                  <th key={h} style={{ textAlign:'left', padding:'8px 10px', borderBottom:'1px solid var(--border)', fontSize:11, color:'var(--text3)', textTransform:'uppercase' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {status.vendors.map(v => (
                <React.Fragment key={v.vendorId}>
                  <tr style={{ borderBottom:'1px solid var(--border)' }}>
                    <td style={{ padding:'10px 10px', fontWeight:600 }}>{v.name}</td>
                    <td style={{ padding:'10px 10px' }}>{badge(v.tapOnboardingStatus)}</td>
                    <td style={{ padding:'10px 10px', fontFamily:'monospace', fontSize:11, color:'var(--text2)' }}>{v.tapRetailerId || '—'}</td>
                    <td style={{ padding:'10px 10px' }}>
                      <button className="btn btn-ghost btn-sm" onClick={() => viewLogs(v.vendorId)}>
                        {logVendorId === v.vendorId ? '▲ Hide' : '▼ Logs'}
                      </button>
                    </td>
                  </tr>
                  {logVendorId === v.vendorId && logs && (
                    <tr>
                      <td colSpan={4} style={{ padding:'0 10px 12px', background:'var(--bg3)' }}>
                        {logs.length === 0 ? (
                          <div style={{ fontSize:12, color:'var(--text3)', padding:'8px 0' }}>No logs yet.</div>
                        ) : logs.map(log => (
                          <div key={log.id} style={{ display:'flex', gap:10, alignItems:'flex-start', padding:'6px 0', borderBottom:'1px solid var(--border)', fontSize:12 }}>
                            <span style={{ color: log.status === 'SUCCESS' ? 'var(--green)' : 'var(--red)', minWidth:14 }}>
                              {log.status === 'SUCCESS' ? '✓' : '✗'}
                            </span>
                            <div style={{ flex:1 }}>
                              <span style={{ fontWeight:600 }}>{log.step}</span>
                              {log.errorMessage && <span style={{ color:'var(--red)', marginLeft:8 }}>{log.errorMessage}</span>}
                            </div>
                            <div style={{ color:'var(--text3)', whiteSpace:'nowrap' }}>{new Date(log.createdAt).toLocaleString()}</div>
                          </div>
                        ))}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function MallsTab() {
  const [malls,        setMalls]        = useState([]);
  const [search,       setSearch]       = useState('');
  const [selectedId,   setSelectedId]   = useState(null);
  const [admins,       setAdmins]       = useState([]);
  const [showNewMall,  setShowNewMall]  = useState(false);
  const [mallForm,     setMallForm]     = useState(EMPTY_MALL_FORM);
  const [editForm,     setEditForm]     = useState(null);   // { name, city, contactEmail, isActive }
  const [showAddAdmin, setShowAddAdmin] = useState(false);
  const [adminForm,    setAdminForm]    = useState(EMPTY_ADMIN_FORM);
  const [resetAdminId, setResetAdminId] = useState(null);
  const [resetForm,    setResetForm]    = useState(EMPTY_RESET_FORM);
  const [saving,       setSaving]       = useState(false);
  const [msg,          setMsg]          = useState('');

  const flash = (t) => { setMsg(t); setTimeout(() => setMsg(''), 4000); };

  const loadMalls = useCallback(async () => {
    try { const r = await api.get('/super-admin/malls'); setMalls(r.data || []); } catch (_) {}
  }, []);

  const loadAdmins = useCallback(async (mallId) => {
    try { const r = await api.get(`/super-admin/malls/${mallId}/admins`); setAdmins(r.data || []); } catch (_) { setAdmins([]); }
  }, []);

  useEffect(() => { loadMalls(); }, [loadMalls]);

  const selectMall = (mall) => {
    setSelectedId(mall.id);
    setEditForm({ name: mall.name, city: mall.city || '', contactEmail: mall.contactEmail || '', isActive: mall.isActive });
    setShowAddAdmin(false);
    setAdminForm(EMPTY_ADMIN_FORM);
    setResetAdminId(null);
    setResetForm(EMPTY_RESET_FORM);
    loadAdmins(mall.id);
  };

  const handleCreateMall = async () => {
    if (!mallForm.name.trim()) return flash('❌ Mall name is required');
    setSaving(true);
    try {
      await api.post('/super-admin/malls', mallForm);
      flash('✅ Mall created');
      setShowNewMall(false);
      setMallForm(EMPTY_MALL_FORM);
      loadMalls();
    } catch (e) { flash('❌ ' + (e.response?.data?.error || e.message)); }
    finally { setSaving(false); }
  };

  const handleUpdateMall = async () => {
    setSaving(true);
    try {
      await api.patch(`/super-admin/malls/${selectedId}`, editForm);
      flash('✅ Mall updated');
      loadMalls();
    } catch (e) { flash('❌ ' + (e.response?.data?.error || e.message)); }
    finally { setSaving(false); }
  };

  const handleToggleQrActive = async (mall) => {
    const next = !mall.isActive;
    try {
      await api.patch(`/super-admin/mall/${mall.id}/active`, { isActive: next });
      setMalls(ms => ms.map(m => m.id === mall.id ? { ...m, isActive: next } : m));
      if (mall.id === selectedId) setEditForm(f => f && ({ ...f, isActive: next }));
      flash(next ? `✅ QR ordering ON for ${mall.name}` : `✅ QR ordering OFF for ${mall.name}`);
    } catch (e) { flash('❌ ' + (e.response?.data?.error || e.message)); }
  };

  const handleCreateAdmin = async () => {
    if (!adminForm.name || !adminForm.email || !adminForm.password) return flash('❌ All fields are required');
    if (adminForm.password !== adminForm.confirm) return flash('❌ Passwords do not match');
    setSaving(true);
    try {
      await api.post(`/super-admin/malls/${selectedId}/admins`, {
        name: adminForm.name, email: adminForm.email, password: adminForm.password,
      });
      flash('✅ Admin created');
      setShowAddAdmin(false);
      setAdminForm(EMPTY_ADMIN_FORM);
      loadAdmins(selectedId);
    } catch (e) { flash('❌ ' + (e.response?.data?.error || e.message)); }
    finally { setSaving(false); }
  };

  const handleResetPassword = async (adminId) => {
    if (!resetForm.password) return flash('❌ Password is required');
    if (resetForm.password !== resetForm.confirm) return flash('❌ Passwords do not match');
    setSaving(true);
    try {
      await api.patch(`/super-admin/malls/${selectedId}/admins/${adminId}/password`, { newPassword: resetForm.password });
      flash('✅ Password reset');
      setResetAdminId(null);
      setResetForm(EMPTY_RESET_FORM);
    } catch (e) { flash('❌ ' + (e.response?.data?.error || e.message)); }
    finally { setSaving(false); }
  };

  const filtered = malls.filter(m => m.name.toLowerCase().includes(search.toLowerCase()));
  const selected = malls.find(m => m.id === selectedId);

  return (
    <div>
      {msg && (
        <div style={{
          padding: '10px 16px', borderRadius: 8, marginBottom: 16, fontSize: 13,
          background: msg.startsWith('✅') ? '#1a3d2a' : '#3d1a1a',
          color:      msg.startsWith('✅') ? 'var(--green)' : 'var(--red)',
          border: `1px solid ${msg.startsWith('✅') ? 'var(--green)' : 'var(--red)'}`,
        }}>{msg}</div>
      )}

      <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>

        {/* ── LEFT: Mall List ── */}
        <div style={{ width: 380, flexShrink: 0 }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            <input
              placeholder="Search malls…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ flex: 1 }}
            />
            <button className="btn btn-primary btn-sm" onClick={() => { setShowNewMall(true); setMallForm(EMPTY_MALL_FORM); }}>
              ＋ New Mall
            </button>
          </div>

          {showNewMall && (
            <div className="card" style={{ marginBottom: 12, border: '1px solid var(--accent)' }}>
              <div style={{ fontWeight: 700, marginBottom: 12 }}>New Mall</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div>
                  <div className="section-label" style={{ marginBottom: 5 }}>Mall Name *</div>
                  <input value={mallForm.name} onChange={e => setMallForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. City Centre Mirdif" />
                </div>
                <div>
                  <div className="section-label" style={{ marginBottom: 5 }}>City</div>
                  <input value={mallForm.city} onChange={e => setMallForm(f => ({ ...f, city: e.target.value }))} placeholder="e.g. Dubai" />
                </div>
                <div>
                  <div className="section-label" style={{ marginBottom: 5 }}>Contact Email</div>
                  <input type="email" value={mallForm.contactEmail} onChange={e => setMallForm(f => ({ ...f, contactEmail: e.target.value }))} placeholder="admin@mall.com" />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
                <button className="btn btn-primary btn-sm" disabled={saving} onClick={handleCreateMall}>{saving ? '⏳' : 'Create Mall'}</button>
                <button className="btn btn-ghost btn-sm" onClick={() => setShowNewMall(false)}>Cancel</button>
              </div>
            </div>
          )}

          {filtered.length === 0 && (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text3)', fontSize: 13 }}>
              {malls.length === 0 ? 'No malls yet. Create the first one.' : 'No results.'}
            </div>
          )}

          {filtered.map(mall => (
            <div
              key={mall.id}
              onClick={() => selectMall(mall)}
              className="card"
              style={{
                marginBottom: 8, padding: '14px 16px', cursor: 'pointer',
                border: mall.id === selectedId ? '1px solid var(--accent)' : '1px solid var(--border)',
                opacity: mall.isActive ? 1 : 0.6,
              }}
            >
              <div style={{ fontWeight: 700, marginBottom: 3 }}>{mall.name}</div>
              <div style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--text3)', marginBottom: 6 }}>{mall.slug}</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                {mall.city && <span style={{ fontSize: 12, color: 'var(--text2)' }}>{mall.city}</span>}
                <span style={{ fontSize: 11, color: 'var(--text3)' }}>
                  {mall._count?.vendors ?? 0} vendors · {mall._count?.admins ?? 0} admins
                </span>
                <span className={`badge ${mall.isActive ? 'badge-green' : 'badge-red'}`} style={{ fontSize: 10 }}>
                  {mall.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div
                onClick={e => e.stopPropagation()}
                style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}
              >
                <span style={{ fontSize: 12, color: 'var(--text2)' }}>QR Active</span>
                <div className={`toggle${mall.isActive ? ' on' : ''}`}
                  onClick={() => handleToggleQrActive(mall)}
                  style={{ cursor: 'pointer', background: mall.isActive ? 'var(--green)' : 'var(--red)' }} />
                <span style={{ fontSize: 11, fontWeight: 600, color: mall.isActive ? 'var(--green)' : 'var(--red)' }}>
                  {mall.isActive ? 'ON' : 'OFF'}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* ── RIGHT: Mall Detail ── */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {!selected ? (
            <div style={{ textAlign: 'center', padding: 80, color: 'var(--text3)' }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>🏬</div>
              <div>Select a mall to manage it</div>
            </div>
          ) : (
            <>
              {/* Section A — Mall Info */}
              <div className="card" style={{ marginBottom: 20 }}>
                <div style={{ fontWeight: 700, marginBottom: 4 }}>Mall Info</div>
                <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 16 }}>
                  Created {new Date(selected.createdAt).toLocaleDateString()}
                </div>

                <div className="grid2" style={{ gap: 12, marginBottom: 12 }}>
                  <div>
                    <div className="section-label" style={{ marginBottom: 5 }}>Mall Name</div>
                    <input value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} />
                  </div>
                  <div>
                    <div className="section-label" style={{ marginBottom: 5 }}>City</div>
                    <input value={editForm.city} onChange={e => setEditForm(f => ({ ...f, city: e.target.value }))} placeholder="e.g. Dubai" />
                  </div>
                  <div>
                    <div className="section-label" style={{ marginBottom: 5 }}>Contact Email</div>
                    <input type="email" value={editForm.contactEmail} onChange={e => setEditForm(f => ({ ...f, contactEmail: e.target.value }))} placeholder="admin@mall.com" />
                  </div>
                  <div>
                    <div className="section-label" style={{ marginBottom: 5 }}>Slug (read-only)</div>
                    <input
                      value={selected.slug}
                      readOnly
                      title="Slug cannot be changed after creation — it is part of all QR URLs for this mall"
                      style={{ color: 'var(--text3)', cursor: 'default' }}
                    />
                    <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>
                      Cannot be changed — embedded in QR codes
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                  <div
                    className={`toggle${editForm.isActive ? ' on' : ''}`}
                    onClick={() => setEditForm(f => ({ ...f, isActive: !f.isActive }))}
                    style={{ cursor: 'pointer' }}
                  />
                  <span style={{ fontSize: 13, color: 'var(--text2)' }}>
                    {editForm.isActive ? 'Active — kiosk is live' : 'Inactive — kiosk disabled'}
                  </span>
                </div>

                <button className="btn btn-primary btn-sm" disabled={saving} onClick={handleUpdateMall}>
                  {saving ? '⏳ Saving…' : '💾 Save Changes'}
                </button>
              </div>

              {/* Section B — Mall Admins */}
              <div className="card" style={{ marginBottom: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <div style={{ fontWeight: 700 }}>Admin Accounts</div>
                  <button className="btn btn-primary btn-sm" onClick={() => { setShowAddAdmin(v => !v); setAdminForm(EMPTY_ADMIN_FORM); }}>
                    {showAddAdmin ? 'Cancel' : '＋ Add Admin'}
                  </button>
                </div>

                {showAddAdmin && (
                  <div style={{ background: 'var(--bg3)', borderRadius: 8, padding: 16, marginBottom: 16, border: '1px solid var(--border)' }}>
                    <div className="grid2" style={{ gap: 12, marginBottom: 12 }}>
                      <div>
                        <div className="section-label" style={{ marginBottom: 5 }}>Name *</div>
                        <input value={adminForm.name} onChange={e => setAdminForm(f => ({ ...f, name: e.target.value }))} placeholder="Full name" />
                      </div>
                      <div>
                        <div className="section-label" style={{ marginBottom: 5 }}>Email *</div>
                        <input type="email" value={adminForm.email} onChange={e => setAdminForm(f => ({ ...f, email: e.target.value }))} placeholder="admin@mall.com" />
                      </div>
                      <div>
                        <div className="section-label" style={{ marginBottom: 5 }}>Password *</div>
                        <input type="password" value={adminForm.password} onChange={e => setAdminForm(f => ({ ...f, password: e.target.value }))} placeholder="Temporary password" />
                      </div>
                      <div>
                        <div className="section-label" style={{ marginBottom: 5 }}>Confirm Password *</div>
                        <input type="password" value={adminForm.confirm} onChange={e => setAdminForm(f => ({ ...f, confirm: e.target.value }))} placeholder="Repeat password" />
                      </div>
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--accent)', marginBottom: 12 }}>
                      Share this password with the mall admin securely. They can reset it after first login.
                    </div>
                    <button className="btn btn-primary btn-sm" disabled={saving} onClick={handleCreateAdmin}>
                      {saving ? '⏳' : 'Create Admin'}
                    </button>
                  </div>
                )}

                {admins.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: 32, color: 'var(--text3)', fontSize: 13 }}>
                    No admin accounts yet.
                  </div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr>
                        {['Name', 'Email', 'Created', 'Actions'].map(h => (
                          <th key={h} style={{ textAlign: 'left', padding: '9px 12px', borderBottom: '1px solid var(--border)', fontSize: 11, color: 'var(--text3)', textTransform: 'uppercase' }}>
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {admins.map(admin => (
                        <React.Fragment key={admin.id}>
                          <tr style={{ borderBottom: '1px solid var(--border)' }}>
                            <td style={{ padding: '11px 12px', fontWeight: 600 }}>{admin.name}</td>
                            <td style={{ padding: '11px 12px', color: 'var(--text2)', fontFamily: 'monospace', fontSize: 12 }}>{admin.email}</td>
                            <td style={{ padding: '11px 12px', color: 'var(--text3)', whiteSpace: 'nowrap' }}>
                              {new Date(admin.createdAt).toLocaleDateString()}
                            </td>
                            <td style={{ padding: '11px 12px' }}>
                              <button
                                className="btn btn-ghost btn-sm"
                                style={{ fontSize: 12 }}
                                onClick={() => {
                                  setResetAdminId(resetAdminId === admin.id ? null : admin.id);
                                  setResetForm(EMPTY_RESET_FORM);
                                }}
                              >
                                🔑 Reset Password
                              </button>
                            </td>
                          </tr>
                          {resetAdminId === admin.id && (
                            <tr>
                              <td colSpan={4} style={{ padding: '0 12px 12px' }}>
                                <div style={{ background: 'var(--bg3)', borderRadius: 8, padding: 14, border: '1px solid var(--border)' }}>
                                  <div style={{ display: 'flex', gap: 12, marginBottom: 10, flexWrap: 'wrap' }}>
                                    <div style={{ flex: 1, minWidth: 160 }}>
                                      <div className="section-label" style={{ marginBottom: 5 }}>New Password</div>
                                      <input type="password" value={resetForm.password} onChange={e => setResetForm(f => ({ ...f, password: e.target.value }))} placeholder="New password" />
                                    </div>
                                    <div style={{ flex: 1, minWidth: 160 }}>
                                      <div className="section-label" style={{ marginBottom: 5 }}>Confirm</div>
                                      <input type="password" value={resetForm.confirm} onChange={e => setResetForm(f => ({ ...f, confirm: e.target.value }))} placeholder="Repeat password" />
                                    </div>
                                  </div>
                                  <div style={{ display: 'flex', gap: 8 }}>
                                    <button className="btn btn-primary btn-sm" disabled={saving} onClick={() => handleResetPassword(admin.id)}>
                                      {saving ? '⏳' : 'Save'}
                                    </button>
                                    <button className="btn btn-ghost btn-sm" onClick={() => { setResetAdminId(null); setResetForm(EMPTY_RESET_FORM); }}>
                                      Cancel
                                    </button>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Section C — Tap Marketplace Onboarding */}
              <TapOnboardingSection mallId={selected.id} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
